import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Card,
  CardContent,
  Alert,
  Avatar,
} from '@mui/material';
import ResponsiveDataGrid from '../components/ResponsiveDataGrid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { 
  Add, 
  Edit, 
  Delete, 
  Warning, 
  CheckCircle, 
  Cancel,
  Payment as PaymentIcon
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { formatINR } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import { ACCENTS, tint, colorForName } from '../theme/accents';
import StatCard from '../components/StatCard';
import MemberDetailDialog from '../components/MemberDetailDialog';
import { StatusIndicator, StatusOptionContent } from '../components/Indicators';

// DataGrid renders this instead of a bare "No rows" line.
const NoRows = () => (
  <EmptyState
    icon={<Warning />}
    accent={ACCENTS.vermilion}
    title={'No penalties recorded'}
    description={'Nothing has been charged for the current filters. Penalties appear here when a contribution runs late or a member arrives late to a meeting.'}
  />
);

const Penalties = () => {
  const { isAdmin } = useAuth();
  const [penalties, setPenalties] = useState([]);
  const [detailMemberId, setDetailMemberId] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPenalty, setSelectedPenalty] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    totalAmount: 0,
    pendingAmount: 0
  });
  const [filters, setFilters] = useState({
    userId: '',
    status: '',
    fromDate: null,
    toDate: null,
  });
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    amount: 500,
    reason: 'Late arrival to meeting',
    penaltyDate: dayjs(),
    meetingDate: null,
    notes: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  // The standard penalty lives in system_settings, so the form default and the
  // help text below both follow an admin edit instead of going stale.
  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/rules/settings');
      setSettings(response.data);
      setFormData((previous) => ({ ...previous, amount: response.data.penaltyAmount ?? previous.amount }));
    } catch (error) {
      // Falls back to the existing default
    }
  };

  useEffect(() => {
    fetchPenalties();
    fetchUsers();
  }, [filters]);

  useEffect(() => {
    calculateStats();
  }, [penalties]);

  const fetchPenalties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate.format('YYYY-MM-DD'));
      if (filters.toDate) params.append('toDate', filters.toDate.format('YYYY-MM-DD'));
      
      const response = await axios.get(`/api/penalties?${params}`);
      setPenalties(response.data);
    } catch (error) {
      toast.error('Failed to fetch penalties');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // The system admin is not a paying member of the group, so it must not be
      // offered anywhere member figures are recorded — see Members/Loans, which
      // already filter this way.
      const response = await axios.get('/api/users');
      setUsers(response.data.filter((user) => !user.is_system_admin));
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const calculateStats = () => {
    const total = penalties.length;
    const pending = penalties.filter(p => p.status === 'pending').length;
    const paid = penalties.filter(p => p.status === 'paid').length;
    const totalAmount = penalties.reduce((sum, p) => sum + parseFloat(p.amount), 0);
    const pendingAmount = penalties
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + parseFloat(p.amount), 0);

    setStats({ total, pending, paid, totalAmount, pendingAmount });
  };

  const handleOpenDialog = (penalty = null) => {
    if (penalty) {
      setEditMode(true);
      setSelectedPenalty(penalty);
      setFormData({
        userId: penalty.user_id,
        amount: penalty.amount,
        reason: penalty.reason,
        penaltyDate: dayjs(penalty.penalty_date),
        meetingDate: penalty.meeting_date ? dayjs(penalty.meeting_date) : null,
        notes: penalty.notes || '',
      });
    } else {
      setEditMode(false);
      setSelectedPenalty(null);
      setFormData({
        userId: '',
        amount: 500,
        reason: 'Late arrival to meeting',
        penaltyDate: dayjs(),
        meetingDate: null,
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedPenalty(null);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        penaltyDate: formData.penaltyDate.format('YYYY-MM-DD'),
        meetingDate: formData.meetingDate ? formData.meetingDate.format('YYYY-MM-DD') : null,
      };

      if (editMode) {
        await axios.put(`/api/penalties/${selectedPenalty.id}`, payload);
        toast.success('Penalty updated successfully');
      } else {
        await axios.post('/api/penalties', payload);
        toast.success('Penalty created successfully');
      }
      
      handleCloseDialog();
      fetchPenalties();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this penalty?')) {
      try {
        await axios.delete(`/api/penalties/${id}`);
        toast.success('Penalty deleted successfully');
        fetchPenalties();
      } catch (error) {
        toast.error('Failed to delete penalty');
      }
    }
  };

  const handleMarkAsPaid = async (id) => {
    try {
      await axios.put(`/api/penalties/${id}/pay`);
      toast.success('Penalty marked as paid');
      fetchPenalties();
    } catch (error) {
      toast.error('Failed to update penalty status');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 64 },
    {
      field: 'user_name',
      headerName: 'Member',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
          <Avatar
            sx={{
              width: 30,
              height: 30,
              fontSize: 13,
              fontWeight: 700,
              bgcolor: tint(colorForName(params.value || ''), 0.2),
              color: colorForName(params.value || ''),
              border: '1px solid',
              borderColor: tint(colorForName(params.value || ''), 0.5),
            }}
          >
            {(params.value || '?').charAt(0)}
          </Avatar>
          <Typography variant="body2" noWrap className="member-name" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'amount', 
      headerName: 'Amount', 
      width: 110,
      renderCell: (params) => formatINR(params.value),
    },
    { field: 'reason', headerName: 'Reason', flex: 1.4, minWidth: 130 },
    { 
      field: 'penalty_date', 
      headerName: 'Charged', 
      width: 115,
      renderCell: (params) => dayjs(params.value).format('MMM D, YYYY'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => <StatusIndicator status={params.value} size="small" />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          {isAdmin && (
            <>
              {params.row.status === 'pending' && (
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); handleMarkAsPaid(params.row.id); }}
                  color="success"
                  title="Mark as Paid"
                >
                  <PaymentIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); handleOpenDialog(params.row); }}
                title="Edit"
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); handleDelete(params.row.id); }}
                color="error"
                title="Delete"
              >
                <Delete fontSize="small" />
              </IconButton>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Money owed"
        accent={ACCENTS.vermilion}
        title="Penalties"
        subtitle="Late contributions and late arrivals, and what is still to collect."
        actions={isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            Add Penalty
          </Button>
        )}
      />

      {/* Statistics Cards */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Count" value={stats.total} subtitle="All time" icon={<Warning />} accent={ACCENTS.indigo} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Pending" value={stats.pending} subtitle="Unpaid" icon={<Cancel />} accent={ACCENTS.vermilion} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Paid" value={stats.paid} subtitle="Settled" icon={<CheckCircle />} accent={ACCENTS.emerald} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Levied" value={formatINR(stats.totalAmount)} subtitle="Charged so far" icon={<PaymentIcon />} accent={ACCENTS.marigold} />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatCard title="Owed" value={formatINR(stats.pendingAmount)} subtitle="To collect" icon={<Warning />} accent={ACCENTS.magenta} />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Filter by Member"
              value={filters.userId}
              onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Members</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.full_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Filter by Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              {['pending', 'paid'].map(v => (
                <MenuItem key={v} value={v}><StatusOptionContent status={v} /></MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="From Date"
              value={filters.fromDate}
              onChange={(newValue) => setFilters({ ...filters, fromDate: newValue })}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="To Date"
              value={filters.toDate}
              onChange={(newValue) => setFilters({ ...filters, toDate: newValue })}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Common Penalty Reasons Alert */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Standard penalty:</strong>{' '}
          {settings
            ? `${formatINR(settings.penaltyAmount)} — charged for late arrival at a meeting, and for a contribution still unpaid after the ${settings.latePenaltyDays}th of the month.`
            : 'Charged for late arrival at a meeting and for contributions left unpaid past the due date.'}
          {' '}Any amount can be entered manually when the group agrees otherwise.
        </Typography>
      </Alert>

      {/* Data Grid */}
      <Paper sx={{ width: '100%' }}>
        <ResponsiveDataGrid
          mobile={{ title: 'user_name', amount: 'amount', hide: ['id'] }}
          rows={penalties}
          columns={columns}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onRowClick={(params) => setDetailMemberId(params.row.user_id)}
          autoHeight
          loading={loading}
          slots={{ noRowsOverlay: NoRows }}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            // v6 collapses the no-rows overlay under autoHeight, clipping it
            '& .MuiDataGrid-overlayWrapper': { minHeight: 300 },
            '& .MuiDataGrid-overlayWrapperInner': { minHeight: 300 },
            '& .MuiDataGrid-cell:hover': { color: 'primary.main' },
          }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Penalty' : 'Add New Penalty'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Member"
                value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                required
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.full_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                InputProps={{
                  startAdornment: '₹',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
              >
                <MenuItem value="Late arrival to meeting">Late arrival to meeting</MenuItem>
                <MenuItem value="Absence without notice">Absence without notice</MenuItem>
                <MenuItem value="Missing payment deadline">Missing payment deadline</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Penalty Date"
                value={formData.penaltyDate}
                onChange={(newValue) => setFormData({ ...formData, penaltyDate: newValue })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Meeting Date (Optional)"
                value={formData.meetingDate}
                onChange={(newValue) => setFormData({ ...formData, meetingDate: newValue })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <MemberDetailDialog
        memberId={detailMemberId}
        open={Boolean(detailMemberId)}
        onClose={() => setDetailMemberId(null)}
      />
    </Box>
  );
};

export default Penalties;